import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/App";
import { UI, tr } from "@/i18n";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { track } from "@/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [state, setState] = useState("processing"); // processing | paid | pending | failed
  const [token, setToken] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }
    let tries = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      tries += 1;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.reportToken) setToken(r.data.reportToken);
        if (r.data.payment_status === "paid") {
          const dk = `om_purchase_${sessionId}`;
          if (!sessionStorage.getItem(dk)) { sessionStorage.setItem(dk, "1"); track("purchase_completed", { value: 19, currency: "EUR", transaction_id: sessionId }); }
          setState("paid"); return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* keep trying */ }
      if (tries >= 12) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, []);

  const goReport = () => navigate(`/onematch/report/${token}/detailed`);

  return (
    <div className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="payment-success-page">
      {state === "processing" && (
        <>
          <div className="w-12 h-12 border-2 border-navy/20 border-t-navy rounded-full animate-spin mx-auto" />
          <p className="mt-6 text-muted-foreground">{tr(lang, UI.pay_processing)}</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 text-gold mx-auto" />
          <h1 className="mt-5 font-display text-3xl text-navy-deep" data-testid="payment-paid">{tr(lang, UI.pay_success)}</h1>
          <button data-testid="payment-view-report" onClick={goReport} disabled={!token}
            className="mt-7 inline-flex items-center gap-2 bg-navy text-white px-7 py-4 rounded-sm font-bold hover:bg-navy-deep transition-colors duration-300 disabled:opacity-50">
            {tr(lang, UI.pay_view_report)} <ArrowRight className="w-4 h-4" />
          </button>
        </>
      )}
      {state === "pending" && (
        <>
          <div className="w-12 h-12 border-2 border-navy/20 border-t-navy rounded-full animate-spin mx-auto" />
          <p className="mt-6 text-muted-foreground" data-testid="payment-pending">{tr(lang, UI.pay_pending)}</p>
          {token && <button onClick={goReport} className="mt-5 text-navy underline text-sm">{tr(lang, UI.pay_view_report)}</button>}
        </>
      )}
      {state === "failed" && (
        <>
          <XCircle className="w-16 h-16 text-red-500 mx-auto" />
          <p className="mt-6 text-muted-foreground" data-testid="payment-failed">{tr(lang, UI.pay_failed)}</p>
        </>
      )}
    </div>
  );
};

export default PaymentSuccess;
